import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

const schema = z.object({
    email: z.string().email("Enter a valid email"),
    password: z.string().min(1, "Password is required"),
});

export default function LoginPage() {
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState(null);

    const from = location.state?.from || "/";

    // Already logged in? Bounce to home.
    React.useEffect(() => {
        if (isAuthenticated) navigate(from, { replace: true });
    }, [isAuthenticated, from, navigate]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: zodResolver(schema) });

    const onSubmit = async (values) => {
        setSubmitting(true);
        setServerError(null);
        try {
            const user = await login(values.email, values.password);
            toast.success(`Welcome back, ${user.full_name.split(" ")[0]}`);
            navigate(from, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Login failed. Please try again.";
            setServerError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background grain-bg flex flex-col">
            <div className="flex flex-1 items-center justify-center px-4 py-12">
                <div className="w-full max-w-md animate-fade-in-up">
                    <div className="mb-8 flex justify-center">
                        <img
                            src="/happylab-logo-full.png"
                            alt="Happy Lab"
                            className="h-auto w-40 object-contain"
                            data-testid="auth-brand-logo"
                        />
                    </div>

                    <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                        Welcome back
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Sign in to keep practising. Calm, focused, no judgement.
                    </p>

                    <Card className="mt-8 border-border/80 shadow-sm">
                        <CardContent className="p-6">
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-5"
                                data-testid="login-form"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="you@example.com"
                                        data-testid="login-email-input"
                                        {...register("email")}
                                    />
                                    {errors.email && (
                                        <p className="text-xs text-destructive" data-testid="login-email-error">
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="Your password"
                                        data-testid="login-password-input"
                                        {...register("password")}
                                    />
                                    {errors.password && (
                                        <p className="text-xs text-destructive" data-testid="login-password-error">
                                            {errors.password.message}
                                        </p>
                                    )}
                                </div>

                                {serverError && (
                                    <div
                                        className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                                        data-testid="login-server-error"
                                    >
                                        {serverError}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    size="lg"
                                    className="w-full bg-primary hover:bg-primary-hover"
                                    disabled={submitting}
                                    data-testid="login-submit-button"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Signing in…
                                        </>
                                    ) : (
                                        <>
                                            Sign in
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>

                            <p className="mt-6 text-sm text-muted-foreground">
                                Don&apos;t have an account?{" "}
                                <Link
                                    to="/register"
                                    className="font-medium text-primary hover:underline"
                                    data-testid="goto-register-link"
                                >
                                    Register
                                </Link>
                            </p>
                        </CardContent>
                    </Card>

                    <p className="mt-6 text-xs text-muted-foreground">
                        Try the demo: <span className="font-medium text-foreground">demo@happylab.app</span> /{" "}
                        <span className="font-medium text-foreground">demo1234</span>
                    </p>
                </div>
            </div>
        </div>
    );
}
